import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import InputField from "@medoma/components/InputField/InputField";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { updateAssetName } from "@/api/Equipment";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./AssetDetails.module.scss";

type NameRowProps = {
  assetId: string;
  name: string;
};

export const NameRow = ({ assetId, name }: NameRowProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<{ name: string }>({ defaultValues: { name } });

  const { mutate: saveName, isPending } = useMutation({
    mutationFn: (nextName: string) =>
      updateAssetName({ assetId, name: nextName }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setIsEditing(false);
    },
    onError: (error) => {
      setError("root.server", {
        message: `${t`Kunde inte byta namn på utrustningen.`} ${deducedError(error)}`,
      });
    },
  });

  const cancelEdit = () => {
    reset({ name });
    setIsEditing(false);
  };

  return (
    <>
      <DescriptionList.Term>
        <Trans>Namn</Trans>
      </DescriptionList.Term>
      <DescriptionList.Description>
        <div className={styles.fieldWithAction}>
          <Text element="span">{name}</Text>
          <div className={styles.actions}>
            {isEditing ? (
              <PlainButton size="small" onClick={cancelEdit}>
                <Trans>Avbryt</Trans>
              </PlainButton>
            ) : (
              <PlainButton
                size="small"
                aria-label={t`Ändra namn`}
                onClick={() => setIsEditing(true)}
              >
                <Trans>Ändra</Trans>
              </PlainButton>
            )}
          </div>
          {isEditing ? (
            <div className={styles.editContainer}>
              <Form
                onSubmit={handleSubmit(({ name: nextName }) =>
                  saveName(nextName.trim()),
                )}
              >
                <Form.Row>
                  <InputField
                    label={t`Namn`}
                    showOptionalLabel={false}
                    autoComplete="off"
                    errorMessage={errors.name?.message}
                    {...register("name", {
                      required: { value: true, message: t`Ange ett namn` },
                    })}
                  />
                </Form.Row>
                {errors.root?.server?.message ? (
                  <ErrorMessage message={errors.root.server.message} />
                ) : null}
                <FilledButton type="submit" disabled={isPending}>
                  <Trans>Spara</Trans>
                </FilledButton>
              </Form>
            </div>
          ) : null}
        </div>
      </DescriptionList.Description>
    </>
  );
};
